import React, {useMemo} from 'react';
import {DateTime} from "luxon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays, faClock, faImage, faLocationDot, faStar, faXmark} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import type {SpeciesFamilyLink} from "../App";
import {getMacaulayAssetUrl, getMacaulayCatalogNumbers} from "../helpers/macaulayLibrary";
import './infobox-species-location-detail.scss';

const DEFAULT_BIRD_ICON = '/assets/fallback-bird.svg';

export type InfoboxSpeciesLocationDetailProps = {
    taxonomicOrder: number,
    observations?: any[],
    familyLink?: SpeciesFamilyLink,
    onClose?: () => void
}

const formatDate = (date: string) => {
    const dateTime = DateTime.fromISO(date);
    return dateTime.isValid ? dateTime.toFormat('LLL d, yyyy') : date;
}

const formatDateTime = (date: string) => {
    const dateTime = DateTime.fromISO(date);
    return dateTime.isValid ? dateTime.toFormat('LLL d, yyyy, h:mm a') : date;
}

const getObservationTime = (observation: any) => {
    if (Number.isFinite(observation?.properties?.dateMs)) {
        return observation.properties.dateMs;
    }

    const parsedDate = DateTime.fromISO(observation?.properties?.date);
    return parsedDate.isValid ? parsedDate.toMillis() : 0;
}

const getSightingLabel = (count: number) => `${count} ${count === 1 ? 'sighting' : 'sightings'}`;

export function InfoboxSpeciesLocationDetail({observations = [], familyLink, onClose}: InfoboxSpeciesLocationDetailProps) {
    const sortedObservations = useMemo(() => {
        return [...observations].sort((a, b) => getObservationTime(b) - getObservationTime(a));
    }, [observations]);

    const recentObservations = sortedObservations.slice(0, 5);
    const firstObservation = sortedObservations[sortedObservations.length - 1];
    const latestObservation = sortedObservations[0];
    const primaryObservation = sortedObservations[0];
    const species = primaryObservation?.properties?.species;
    const location = primaryObservation?.properties?.locationName;
    const image = primaryObservation?.properties?.image;
    const hasLiferAtLocation = sortedObservations.some(observation => observation?.properties?.isLifer);
    const recentPhotos = recentObservations.flatMap(observation => {
        return getMacaulayCatalogNumbers(observation.properties.mlCatalogNumbers).map(catalogNumber => ({
            catalogNumber
        }));
    });
    const isFallbackImage = !image || image.endsWith(DEFAULT_BIRD_ICON);

    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (event.currentTarget.src.endsWith(DEFAULT_BIRD_ICON)) return;

        event.currentTarget.src = DEFAULT_BIRD_ICON;
        event.currentTarget.classList.add('species-location-card__image-element--fallback');
    }

    if (!primaryObservation) {
        return null;
    }

    return (
        <article className="species-location-card" aria-label={`${species} sightings at ${location}`}>
            {onClose ?
                <button
                    className="species-location-card__close"
                    type="button"
                    aria-label="Close location details"
                    onClick={onClose}
                >
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>
                : null
            }

            <div className="species-location-card__image">
                <img
                    src={image || DEFAULT_BIRD_ICON}
                    alt={species ?? ''}
                    className={isFallbackImage ? 'species-location-card__image-element species-location-card__image-element--fallback' : 'species-location-card__image-element'}
                    onError={handleImageError}
                />
            </div>

            <div className="species-location-card__body">
                <div className="species-location-card__eyebrow">
                    {getSightingLabel(sortedObservations.length)} at this location
                </div>

                {familyLink ?
                    <Link
                        className="species-location-card__family-link"
                        to={`/family-explorer/family/${familyLink.familySlug}`}
                    >
                        {familyLink.familyName}
                    </Link>
                    : null
                }

                {species ?
                    <h2 className="species-location-card__title">{species}</h2>
                    : null
                }

                {location ?
                    <div className="species-location-card__location">
                        <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                        <span>{location}</span>
                    </div>
                    : null
                }

                {hasLiferAtLocation ?
                    <div className="species-location-card__badges" aria-label="Observation highlights">
                        <span className="species-location-card__badge species-location-card__badge--lifer">
                            <FontAwesomeIcon icon={faStar} aria-hidden="true" />
                            Lifer
                        </span>
                    </div>
                    : null
                }

                <dl className="species-location-card__stats">
                    {firstObservation ?
                        <div className="species-location-card__stat">
                            <dt>
                                <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                                <span>First sighting</span>
                            </dt>
                            <dd>{formatDate(firstObservation.properties.date)}</dd>
                        </div>
                        : null
                    }
                    {latestObservation && latestObservation !== firstObservation ?
                        <div className="species-location-card__stat">
                            <dt>
                                <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                                <span>Most recent</span>
                            </dt>
                            <dd>{formatDate(latestObservation.properties.date)}</dd>
                        </div>
                        : null
                    }
                </dl>

                {recentObservations.length ?
                    <section className="species-location-card__recent" aria-labelledby="species-location-recent-title">
                        <h3 id="species-location-recent-title">Recent Sightings</h3>
                        <ol>
                            {recentObservations.map((observation, index) => (
                                <li key={`${observation.properties.date}-${index}`}>
                                    <span>{formatDateTime(observation.properties.date)}</span>
                                    {observation.properties.count ?
                                        <span className="species-location-card__recent-count">
                                            Count: {observation.properties.count}
                                        </span>
                                        : null
                                    }
                                </li>
                            ))}
                        </ol>
                        {recentPhotos.length ?
                            <div className="species-location-card__photos">
                                <h4>Photos</h4>
                                <ul>
                                    {recentPhotos.map((photo, index) => (
                                        <li key={`${photo.catalogNumber}-${index}`}>
                                            <a
                                                href={getMacaulayAssetUrl(photo.catalogNumber)}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <FontAwesomeIcon icon={faImage} aria-hidden="true" />
                                                <span>ML{photo.catalogNumber}</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            : null
                        }
                    </section>
                    : null
                }
            </div>
        </article>
    )
}
